import { useEffect, useRef } from 'react';
import { PIDS, convertVital, formatVital, type UnitSystem, type Vitals } from '../lib/obd/pids.ts';

/**
 * Live readings, shown inside the scan they extend.
 *
 * Streaming reuses the connection from that scan, so this belongs beside its
 * results rather than in a separate view. The control that starts and stops it
 * lives with the other connection controls in the sidebar; this is the output.
 */
export function LivePanel({
  vitals, live, liveKeys, samples, liveHz, onToggleLiveKey, units,
}: {
  vitals: Vitals;
  live: boolean;
  liveKeys: string[];
  samples: Record<string, number[]>;
  liveHz: number | null;
  onToggleLiveKey: (key: string) => void;
  units: UnitSystem;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Starting a stream from the sidebar leaves the readings somewhere down the
  // page, so bring them into view. Only on the transition into streaming, or
  // it would fight the scroll on every sweep.
  useEffect(() => {
    if (live) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [live]);

  return (
    <div className="card" ref={ref}>
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, flex: 1 }}>{live ? 'Live data' : 'Live data at scan'}</h2>
        {live && (
          <span className="pill ok live">
            <i className="dot" />
            streaming{liveHz !== null && ` · ${liveHz.toFixed(1)} Hz`}
          </span>
        )}
      </div>

      {live && (
        <div className="row wrap" style={{ marginBottom: 12, gap: 6 }}>
          {PIDS.map((spec) => (
            <button
              key={spec.key}
              className={`chip${liveKeys.includes(spec.key) ? ' on' : ''}`}
              onClick={() => onToggleLiveKey(spec.key)}
            >
              {spec.label}
            </button>
          ))}
        </div>
      )}

      <div className="vitals">
        {PIDS.map((spec) => {
          const series = samples[spec.key] ?? [];
          const streaming = live && liveKeys.includes(spec.key);
          // While streaming the newest sample is the truth; otherwise the
          // reading taken at scan time stands.
          const value = streaming && series.length > 0 ? series[series.length - 1] : (vitals[spec.key] ?? null);
          const shown = value === null ? null : convertVital(spec, value, units);
          return (
            <div className={`vital${streaming ? ' streaming' : ''}`} key={spec.key}>
              <span className="k">{spec.label}</span>
              <div className="v">
                {shown === null
                  ? '—'
                  : shown.value.toLocaleString(undefined, { maximumFractionDigits: shown.precision })}
                {shown !== null && <span className="u">{shown.unit}</span>}
              </div>
              {streaming && series.length > 1 && (
                <Sparkline series={series} spec={spec} units={units} label={spec.label} />
              )}
            </div>
          );
        })}
      </div>

      <p className="small dim" style={{ marginBottom: 0, marginTop: 10 }}>
        {live
          ? 'Streaming reuses the connection from the last scan. Fewer channels refresh faster, so switch off the ones you are not watching.'
          : 'A dash means the vehicle does not report that PID, which is normal and not a fault.'}
      </p>
    </div>
  );
}

function Sparkline({
  series,
  spec,
  units,
  label,
}: {
  series: number[];
  spec: (typeof PIDS)[number];
  units: UnitSystem;
  label: string;
}) {
  const width = 100;
  const height = 24;
  const pad = 2;

  const min = Math.min(...series);
  const max = Math.max(...series);
  const span = max - min;

  const x = (i: number) => (series.length === 1 ? width : (i / (series.length - 1)) * width);
  // A flat series sits on the centre line rather than collapsing to an edge.
  const y = (v: number) => (span === 0 ? height / 2 : pad + (1 - (v - min) / span) * (height - pad * 2));

  const points = series.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const lastX = x(series.length - 1);
  const lastY = y(series[series.length - 1]);

  return (
    <svg
      className="spark"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`${label} trend, ${series.length} samples, ranging ${formatVital(spec, min, units)} to ${formatVital(spec, max, units)}`}
    >
      <line x1="0" y1={height - 0.5} x2={width} y2={height - 0.5} className="spark-base" />
      <polyline points={points} className="spark-line" vectorEffect="non-scaling-stroke" />
      <circle cx={lastX} cy={lastY} r="2.5" className="spark-head" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
